import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { dailyNormEditDialog as t } from '@/styles/tokens';
import { ModalIndices } from '@/common/i-doc/i-doc.registry';
import { NormDialogShell, NormInput, RecommendedBlock, RecommendedValue } from './dailyNormDialogParts';

export interface DailyNormEditConfig {
  /** Назва норми в заголовку модалки. */
  title: string;
  fieldLabel: string;
  unit: string;
  value: number | null;
  recommended: number | null;
  /** Іконка й колір значення рекомендованої норми. */
  recommendedIcon: React.ReactElement;
  recommendedColor: string;
  recommendedHint?: string;
  /** Підпис кнопки формули в блоці рекомендованої норми (напр. "BMR"). */
  formulaLabels?: string[];
}

interface DailyNormEditDialogProps {
  open: boolean;
  onClose: () => void;
  config: DailyNormEditConfig | null;
  /** null - поле очищено (скинути до стандартної норми). */
  onSave: (value: number | null) => void;
  /** Кнопка формули - пояснення розрахунку. */
  onFormulaClick?: (label: string) => void;
  saving?: boolean;
}

// Редагування однієї денної норми клієнта (калорії, вода) - Figma-макет.
export const DailyNormEditDialog: React.FC<DailyNormEditDialogProps> = ({ open, onClose, config, onSave, saving, onFormulaClick }) => {
  const { t: tr } = useTranslation();
  const [value, setValue] = useState('');

  useEffect(() => {
    if (open) setValue(config?.value != null ? String(config.value) : '');
  }, [open, config]);

  if (!config) return null;

  return (
    <NormDialogShell open={open} onClose={onClose} title={tr('clientProfile.dailyNorms.edit.title', { name: config.title })} iDoc={ModalIndices.DAILY_NORM_EDIT} saving={saving} onSave={() => onSave(value.trim() === '' ? null : Number(value))}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography component="label" htmlFor="daily-norm-value" sx={{ fontSize: 14, lineHeight: '16px', fontWeight: 500, color: t.textMuted }}>
          {config.fieldLabel}*
        </Typography>
        <NormInput id="daily-norm-value" unit={config.unit} value={value} onChange={(e) => setValue(e.target.value)} />
      </Box>

      {config.recommended != null && (
        <RecommendedBlock hint={config.recommendedHint} formulaLabels={config.formulaLabels} onFormulaClick={onFormulaClick}>
          <RecommendedValue color={config.recommendedColor} icon={config.recommendedIcon}>
            {config.recommended} {config.unit}
          </RecommendedValue>
        </RecommendedBlock>
      )}
    </NormDialogShell>
  );
};
